/**
 * component-quantity-offer.js
 *
 * Listens to the product-page quantity selector and dynamically updates the
 * displayed price + offer banner based on the `multi_add_offer` metafield tiers
 * configured on the current variant.
 *
 * Usage: rendered by product-info.liquid (price block) when `show_multi_add_offer`
 * is enabled and the variant has at least one multi_add_offer with min_quantity > 1.
 *
 * Data contract (JSON data island inside the element):
 *   [{ min_quantity, discount_value, discount_unit, discount_name, discount_small_description }]
 *
 * Responds to Shopify variant re-renders: connectedCallback re-initialises from the
 * freshly-rendered DOM, disconnectedCallback aborts all listeners.
 */

if (typeof QuantityOffer === 'undefined') {
  class QuantityOffer extends HTMLElement {
    // ─── Config ────────────────────────────────────────────────────────────────
    static SELECTORS = {
      offersData:     'script[type="application/json"]',
      banner:         '.multi-add__promo-banner',
      priceList:      'price-list',
      salePrice:      'sale-price',
      compareAtPrice: 'compare-at-price',
      section:        '.shopify-section',
      productForm:    'product-form form[action*="/cart/add"]',
    };

    static QUANTITY_FIELD = 'quantity';

    static CLASSES = {
      saleOnSale:    'text-on-sale',
      compareThrough: 'line-through',
    };

    static PRICE_CLASS_BASE = 'text--large';

    static EVENTS = { change: 'quantity-offer:change' };

    // ─── Lifecycle ──────────────────────────────────────────────────────────────
    connectedCallback() {
      this._abort      = new AbortController();
      this._offers     = this._parseOffers();
      this._basePrice  = this._toInt(this.dataset.basePrice, 0);
      this._banner     = this.querySelector(QuantityOffer.SELECTORS.banner);

      if (!this._offers.length || !this._basePrice) return;

      // Scoped to the section: the first cart/add form on the page is Shopify's payment-terms form.
      const scope = this.closest(QuantityOffer.SELECTORS.section) ?? document;
      const form  = scope.querySelector(QuantityOffer.SELECTORS.productForm);
      if (!form) return;

      // form.elements also covers the selector input linked via the `form` attribute, outside the <form>.
      this._input = form.elements.namedItem(QuantityOffer.QUANTITY_FIELD);
      if (!(this._input instanceof HTMLInputElement)) return;

      const opts = { signal: this._abort.signal };
      this._input.addEventListener('change', () => this._update(), opts);
      this._input.addEventListener('input',  () => this._update(), opts);

      this._update();
    }

    disconnectedCallback() {
      this._abort?.abort();
    }

    // ─── Public API (read by multi-add-summary) ─────────────────────────────────
    get formId() {
      // Not `form.id`: the form's <input name="id"> (variant) shadows it.
      return this._input?.form?.getAttribute('id') ?? null;
    }

    /** Discounted unit price in cents for `qty`, or null when no tier applies. */
    unitPriceFor(qty) {
      const offer = this._activeOffer(qty);
      return offer ? this._discountedUnitPrice(offer) : null;
    }

    // ─── Data parsing ───────────────────────────────────────────────────────────
    _parseOffers() {
      try {
        return JSON.parse(
          this.querySelector(QuantityOffer.SELECTORS.offersData)?.textContent ?? '[]'
        );
      } catch {
        return [];
      }
    }

    // ─── Helpers ────────────────────────────────────────────────────────────────
    _toInt(value, fallback) {
      const n = parseInt(value, 10);
      return Number.isNaN(n) ? fallback : n;
    }

    _currentQty() {
      return Math.max(1, this._toInt(this._input?.value, 1));
    }

    /** Returns the highest-tier offer whose min_quantity is ≤ qty, or null.
     *  Offers without a min_quantity field are skipped — they are static
     *  single-unit offers handled by price-list.liquid, not by this component. */
    _activeOffer(qty) {
      return [...this._offers]
        .filter(o => o.min_quantity != null && qty >= o.min_quantity)
        .sort((a, b) => b.min_quantity - a.min_quantity)[0] ?? null;
    }

    /** Returns discounted unit price in cents. */
    _discountedUnitPrice(offer) {
      if (!offer) return this._basePrice;
      const { discount_value: val, discount_unit: unit } = offer;
      if (unit === '%' || unit === 'percent') {
        return Math.round(this._basePrice * (100 - val) / 100);
      }
      if (unit === '€') {
        return Math.max(0, this._basePrice - val * 100);
      }
      return this._basePrice;
    }

    // Matches the theme's money_without_trailing_zeros output ("99€", "148,50€").
    _formatMoney(cents) {
      const decimals = cents % 100 === 0 ? 0 : 2;
      return new Intl.NumberFormat('fr-FR', {
        style:                'currency',
        currency:             'EUR',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(cents / 100).replace(/\s€/, '€');
    }

    _escHtml(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g,  '&lt;')
        .replace(/>/g,  '&gt;')
        .replace(/"/g,  '&quot;');
    }

    // ─── Update cycle ───────────────────────────────────────────────────────────
    _update() {
      const qty            = this._currentQty();
      const offer          = this._activeOffer(qty);
      const unitDiscounted = this._discountedUnitPrice(offer);
      const totalDiscounted = unitDiscounted * qty;
      const totalBase       = this._basePrice * qty;

      this._updateBanner(offer);
      this._updatePriceList(totalDiscounted, totalBase, offer !== null);
      this._dispatchChange();
    }

    _dispatchChange() {
      this.dispatchEvent(new CustomEvent(QuantityOffer.EVENTS.change, {
        bubbles: true,
        detail:  { formId: this.formId },
      }));
    }

    _updateBanner(offer) {
      if (!this._banner) return;
      if (!offer) {
        this._banner.hidden = true;
        return;
      }
      this._banner.innerHTML = `
        <span class="multi-add__badge multi-add__badge--black flex align-center gap-1">${this._escHtml(offer.discount_name)}</span>
        <span class="multi-add__badge multi-add__badge--promo">-${offer.discount_value}${this._escHtml(offer.discount_unit)}</span>
        <span class="multi-add__promo-text">${this._escHtml(offer.discount_small_description)}</span>
      `;
      this._banner.hidden = false;
    }

    _updatePriceList(discountedTotal, baseTotal, isOnSale) {
      const priceList = this.parentElement?.querySelector(QuantityOffer.SELECTORS.priceList);
      if (!priceList) return;

      this._setSalePrice(priceList, discountedTotal, isOnSale);
      this._setCompareAtPrice(priceList, baseTotal, isOnSale);
    }

    _setSalePrice(priceList, amountCents, isOnSale) {
      const el = priceList.querySelector(QuantityOffer.SELECTORS.salePrice);
      if (!el) return;

      this._setPriceText(el, this._formatMoney(amountCents));
      el.classList.toggle(QuantityOffer.CLASSES.saleOnSale, isOnSale);
    }

    /** Replaces the price text, keeping the sr-only span (the first text node is Liquid whitespace). */
    _setPriceText(el, formatted) {
      [...el.childNodes].filter(n => n.nodeType === Node.TEXT_NODE).forEach(n => n.remove());
      el.append(formatted);
    }

    _setCompareAtPrice(priceList, amountCents, isOnSale) {
      let el = priceList.querySelector(QuantityOffer.SELECTORS.compareAtPrice);

      if (!isOnSale) {
        if (el) el.hidden = true;
        return;
      }

      const formatted = this._formatMoney(amountCents);

      if (!el) {
        el = document.createElement('compare-at-price');
        el.className = `${QuantityOffer.PRICE_CLASS_BASE} ${QuantityOffer.CLASSES.compareThrough}`;
        el.innerHTML = `<span class="sr-only">Prix normal</span>`;
        priceList.append(el);
      }

      this._setPriceText(el, formatted);
      el.hidden = false;
    }
  }

  window.QuantityOffer = QuantityOffer;
}

if (!customElements.get('quantity-offer')) {
  customElements.define('quantity-offer', window.QuantityOffer);
}
